import { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/hooks/useTheme';
import { useLanguage } from '@/hooks/useLanguage';
import { getCart, getNotifications } from '@/api';
import BrandLogo from '@/components/feature/BrandLogo';
import { brandLogoTone } from '@/lib/placeholders';
import {
  Home,
  LayoutGrid,
  ShoppingCart,
  ClipboardList,
  Heart,
  Store,
  User,
  Bell,
  Search,
  Settings,
  HelpCircle,
  Shield,
  MapPin,
  CreditCard,
  BookOpen,
  Sun,
  Moon,
  ChevronLeft,
  ChevronRight,
  Package,
  Globe,
  Menu,
  X,
} from 'lucide-react';
import AdminBar from '@/components/feature/AdminBar';

export default function DesktopLayout() {
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState('');
  const [userAvatar, setUserAvatar] = useState('');

  // Fetch real data + listen for auth changes
  useEffect(() => {
    const fetchCounts = async (userId?: string) => {
      if (userId) {
        setIsLoggedIn(true);
        const storedName = localStorage.getItem('userName') || '';
        const storedAvatar = localStorage.getItem('userAvatar') || '';
        setUserName(storedName);
        setUserAvatar(storedAvatar);

        // Fetch cart count
        getCart()
          .then((cart: any) => {
            const items = cart?.items || [];
            setCartCount(Array.isArray(items) ? items.length : 0);
          })
          .catch(() => {});

        // Fetch unread notifications count
        getNotifications(1, 1)
          .then((raw: any) => {
            if (typeof raw?.unreadCount === 'number') setUnreadNotifs(raw.unreadCount);
          })
          .catch(() => {});
      } else {
        setIsLoggedIn(false);
        setUserName('');
        setUserAvatar('');
        setCartCount(0);
        setUnreadNotifs(0);
      }
    };

    // Initial check
    const checkAuth = () => {
      const loggedIn = localStorage.getItem('isLoggedIn') === 'true';
      const userId = localStorage.getItem('userId');
      if (loggedIn && userId) {
        fetchCounts(userId);
      } else {
        fetchCounts();
      }
    };

    checkAuth();

    const handleAuthChange = () => checkAuth();
    window.addEventListener('authStateChanged', handleAuthChange);

    // Also check cart changes from other pages
    const handleCartChange = () => {
      const userId = localStorage.getItem('userId');
      if (userId) {
        getCart()
          .then((cart: any) => {
            const items = cart?.items || [];
            setCartCount(Array.isArray(items) ? items.length : 0);
          })
          .catch(() => {});
      }
    };
    window.addEventListener('cartChanged', handleCartChange);

    return () => {
      window.removeEventListener('authStateChanged', handleAuthChange);
      window.removeEventListener('cartChanged', handleCartChange);
    };
  }, []);

  const navItems = [
    { icon: Home, label: t('nav.home'), path: '/' },
    { icon: LayoutGrid, label: t('nav.categories'), path: '/categories' },
    { icon: ShoppingCart, label: t('nav.cart'), path: '/cart' },
    { icon: ClipboardList, label: t('nav.orders'), path: '/orders' },
    { icon: Heart, label: t('nav.wishlist'), path: '/wishlist' },
    { icon: Store, label: t('nav.vendor'), path: '/vendor' },
    { icon: Bell, label: t('nav.notifications'), path: '/notifications' },
    { icon: User, label: t('nav.profile'), path: '/profile' },
  ];

  const moreItems = [
    { icon: MapPin, label: t('nav.addresses'), path: '/addresses' },
    { icon: CreditCard, label: t('nav.paymentMethods'), path: '/payment-methods' },
    { icon: Shield, label: t('nav.security'), path: '/security' },
    { icon: Settings, label: t('nav.settings'), path: '/language' },
    { icon: HelpCircle, label: t('nav.help'), path: '/help-support' },
    { icon: BookOpen, label: t('nav.storyEditor'), path: '/story-studio' },
  ];

  const isActive = (path: string) => location.pathname === path;

  const bgMain = isDark
    ? 'bg-gradient-to-br from-dark via-dark-100 to-[#1A1614]'
    : 'bg-gradient-to-br from-ivory via-cream to-sand';

  const textMain = isDark ? 'text-white' : 'text-gray-900';
  const textMuted = isDark ? 'text-gray-400' : 'text-gray-500';
  const sidebarBg = isDark
    ? 'bg-dark/95 border-white/10'
    : 'bg-white/95 border-gray-200/60';
  const topbarBg = isDark
    ? 'bg-dark/70 border-white/10'
    : 'bg-white/70 border-gray-200/60';
  const cardBg = isDark
    ? 'bg-white/5 hover:bg-white/10 border-white/10'
    : 'bg-white hover:bg-gray-50/80 border-gray-200/60';
  const activeItem = isDark
    ? 'bg-gold/100/20 text-gold'
    : 'bg-gold/10 text-gold';
  const inputBg = isDark
    ? 'bg-white/10 text-white placeholder-gray-400 focus:bg-white/15'
    : 'bg-white/50 text-gray-900 placeholder-gray-500 focus:bg-white/80 border border-white/40';
  const footerBg = isDark
    ? 'bg-dark/60 border-white/10'
    : 'bg-white/60 border-gray-200/60';

  const langs = [
    { code: 'fa', name: t('language.persian'), flag: '🇮🇷' },
    { code: 'en', name: t('language.english'), flag: '🇬🇧' },
    { code: 'de', name: t('language.german'), flag: '🇩🇪' },
    { code: 'tr', name: t('language.turkish'), flag: '🇹🇷' },
  ];

  const currentLang = langs.find((l) => l.code === language) || langs[0];

  const isRTL = language === 'fa';

  return (
    <div className={`relative flex min-h-screen ${bgMain} ${textMain} font-sans transition-colors duration-500`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 bottom-0 z-50 w-64 backdrop-blur-2xl transition-transform duration-500 ease-in-out ${sidebarBg} ${isRTL ? 'right-0 border-l' : 'left-0 border-r'}`}
        style={{
          transform: sidebarOpen
            ? 'translateX(0)'
            : isRTL
              ? 'translateX(100%)'
              : 'translateX(-100%)',
        }}
      >
        <div className="h-full flex flex-col overflow-y-auto">
          {/* Close button */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <Link to="/" className="flex items-center gap-3 group" onClick={() => setSidebarOpen(false)}>
              <BrandLogo className={`w-10 h-10 ${brandLogoTone(isDark)}`} />
              <div className="flex flex-col">
                <span className="text-lg font-bold tracking-tight">{t('common.appName')}</span>
                <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'} -mt-1`}>{t('common.appNameEn')}</span>
              </div>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className={`p-2 rounded-xl transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'} ${textMuted}`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Nav Items */}
          <nav className="flex-1 px-3 py-5 space-y-1.5">
            {navItems.map((item) => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3.5 py-3 rounded-2xl transition-all duration-300 group ${
                    active
                      ? `${activeItem} font-semibold shadow-sm`
                      : `${cardBg} ${textMuted}`
                  }`}
                >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300 ${
                    active
                      ? 'bg-gold/20'
                      : isDark ? 'bg-white/5 group-hover:bg-white/10 border border-white/10' : 'bg-white/50 group-hover:bg-white/70 border border-white/30'
                  }`}>
                    <item.icon className={`w-[18px] h-[18px] transition-colors ${active ? 'text-gold' : textMuted}`} />
                  </div>
                  <span className="text-sm">{item.label}</span>
                  {active && (
                    <div className={`${isRTL ? 'mr-auto' : 'ml-auto'} w-1.5 h-1.5 rounded-full bg-gold/100`} />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Divider */}
          <div className={`mx-4 h-px ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} />

          {/* More Section */}
          <div className="px-3 py-3">
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              className={`flex items-center gap-3 px-3.5 py-3 w-full rounded-2xl transition-all duration-300 ${cardBg} ${textMuted}`}
            >
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                <Package className="w-[18px] h-[18px]" />
              </div>
              <span className="text-sm">{t('nav.more')}</span>
              <ChevronLeft className={`${isRTL ? 'mr-auto' : 'ml-auto'} w-4 h-4 transition-transform duration-300 ${moreOpen ? 'rotate-90' : ''}`} />
            </button>
            {moreOpen && (
              <div className="mt-2 space-y-1 animate-in slide-in-from-top-2 duration-300">
                {moreItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm transition-all ${
                      isActive(item.path)
                        ? `${activeItem} font-semibold`
                        : `${textMuted} hover:${isDark ? 'text-white' : 'text-gray-900'}`
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Bottom: Language + Theme */}
          <div className="px-3 pb-4 pt-2 space-y-2">
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setShowLangMenu(!showLangMenu)}
                className={`flex items-center gap-3 px-3.5 py-3 w-full rounded-2xl transition-all duration-300 ${cardBg} ${textMuted} hover:${isDark ? 'text-white' : 'text-gray-900'}`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isDark ? 'bg-gold/20' : 'bg-gold/10'}`}>
                  <Globe className={`w-[18px] h-[18px] ${isDark ? 'text-gold' : 'text-gold'}`} />
                </div>
                <span className="text-sm">{currentLang.name}</span>
                <span className={`${isRTL ? 'mr-auto' : 'ml-auto'} text-lg`}>{currentLang.flag}</span>
              </button>
              {showLangMenu && (
                <div className={`absolute ${isRTL ? 'right-0' : 'left-0'} bottom-full mb-2 w-full rounded-2xl overflow-hidden ${isDark ? 'bg-dark-100/95 border-white/10' : 'bg-white/95 border-gray-200/50'} border backdrop-blur-xl shadow-2xl z-50`}>
                  {langs.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => {
                        setLanguage(l.code);
                        setShowLangMenu(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 transition-colors ${
                        language === l.code
                          ? `${isDark ? 'bg-gold/100/20 text-gold' : 'bg-gold/10 text-gold'} font-semibold`
                          : `${isDark ? 'hover:bg-white/10 text-gray-300' : 'hover:bg-gray-100 text-gray-700'}`
                      }`}
                    >
                      <span className="text-lg">{l.flag}</span>
                      {l.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`flex items-center gap-3 px-3.5 py-3 w-full rounded-2xl transition-all duration-300 ${cardBg} ${textMuted} hover:${isDark ? 'text-white' : 'text-gray-900'}`}
            >
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isDark ? 'bg-gold/100/20' : 'bg-gold/10'}`}>
                {isDark ? (
                  <Sun className="w-[18px] h-[18px] text-gold" />
                ) : (
                  <Moon className="w-[18px] h-[18px] text-gold" />
                )}
              </div>
              <span className="text-sm">{isDark ? t('theme.lightMode') : t('theme.darkMode')}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 w-full">
        <AdminBar />
        {/* Topbar */}
        <header className={`sticky top-0 z-30 backdrop-blur-2xl border-b px-6 py-4 ${topbarBg}`}>
          <div className="flex items-center justify-between gap-4">
            {/* Left: Hamburger + Greeting */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className={`p-3 rounded-2xl transition-all duration-300 ${cardBg} ${textMuted} hover:${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900'}`}
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="hidden lg:flex items-center gap-3">
                <BrandLogo className={`w-9 h-9 ${brandLogoTone(isDark)}`} />
                <div>
                  <p className={`text-sm font-semibold ${textMain}`}>{t('common.appName')}</p>
                </div>
              </div>
            </div>

            {/* Search */}
            <div className="flex-1 max-w-2xl">
              <div className="relative group">
                <Search className={`absolute ${isRTL ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 w-5 h-5 transition-colors ${isDark ? 'text-gray-500 group-focus-within:text-gold' : 'text-gray-400 group-focus-within:text-gold'}`} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('header.searchPlaceholder')}
                  className={`w-full ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3 rounded-2xl text-sm border-0 outline-none transition-all duration-300 ${inputBg} focus:ring-2 focus:ring-amber-500/30`}
                />
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-2">
              <Link
                to="/notifications"
                className={`relative p-3 rounded-2xl transition-all duration-300 ${cardBg} ${textMuted}`}
              >
                <Bell className="w-5 h-5" />
                {unreadNotifs > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 shadow-lg">
                    {unreadNotifs > 99 ? '99+' : unreadNotifs}
                  </span>
                )}
              </Link>

              <Link
                to="/cart"
                className={`relative p-3 rounded-2xl transition-all duration-300 ${cardBg} ${textMuted}`}
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-gold to-gold-dark text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-lg">
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </Link>

              <Link
                to={isLoggedIn ? '/profile' : '/login'}
                className={`flex items-center gap-3 p-2 ${isRTL ? 'pr-4 pl-2' : 'pl-4 pr-2'} rounded-2xl transition-all duration-300 ${cardBg}`}
              >
                <div className="hidden lg:flex flex-col items-end">
                  <span className={`text-xs font-semibold ${textMain}`}>
                    {isLoggedIn ? (userName || t('header.guest')) : t('auth.login')}
                  </span>
                  <span className={`text-[10px] ${textMuted}`}>
                    {isLoggedIn ? t('header.welcomeBack') : t('header.guest')}
                  </span>
                </div>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center overflow-hidden ${isDark ? 'bg-gradient-to-br from-gold to-gold-dark' : 'bg-gradient-to-br from-gold/15 to-gold/15'}`}>
                  {isLoggedIn && userAvatar ? (
                    <img src={userAvatar} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gold'}`} />
                  )}
                </div>
              </Link>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="pb-0 min-h-[calc(100vh-88px)]">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -12, scale: 0.98 }}
              transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>

        {/* Footer */}
        <footer className={`backdrop-blur-xl border-t px-8 py-10 ${footerBg}`}>
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <BrandLogo className={`w-10 h-10 ${brandLogoTone(isDark)}`} />
                  <div className="flex flex-col">
                    <span className="font-bold text-lg">{t('common.appName')}</span>
                    <span className={`text-[10px] ${textMuted} -mt-1`}>{t('common.appNameEn')}</span>
                  </div>
                </div>
                <p className={`text-sm leading-relaxed ${textMuted}`}>
                  {t('footer.description')}
                </p>
              </div>
              <div>
                <h4 className={`font-semibold mb-4 text-sm ${textMain}`}>{t('footer.quickLinks')}</h4>
                <ul className="space-y-3">
                  {[{ label: t('nav.home'), path: '/' }, { label: t('nav.categories'), path: '/categories' }, { label: t('nav.orders'), path: '/orders' }, { label: t('nav.profile'), path: '/profile' }].map((link) => (
                    <li key={link.path}>
                      <Link to={link.path} className={`text-sm ${textMuted} hover:text-gold transition-colors duration-300`}>
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className={`font-semibold mb-4 text-sm ${textMain}`}>{t('footer.support')}</h4>
                <ul className="space-y-3">
                  {[t('footer.helpCenter'), t('footer.contactUs'), t('footer.terms'), t('footer.privacy')].map((label) => (
                    <li key={label}>
                      <Link to="/help-support" className={`text-sm ${textMuted} hover:text-gold transition-colors duration-300`}>
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className={`font-semibold mb-4 text-sm ${textMain}`}>{t('footer.downloadApp')}</h4>
                <div className="space-y-3">
                  <button className={`w-full py-3 px-4 rounded-2xl text-sm font-medium transition-all duration-300 ${isDark ? 'bg-white/10 hover:bg-white/20 text-white border border-white/10' : 'bg-dark-100/80 hover:bg-dark hover:bg-dark-100 text-white border border-white/20'}`}>
                    {t('footer.appStore')}
                  </button>
                  <button className="w-full py-3 px-4 bg-gradient-to-r from-gold to-gold-dark text-white rounded-2xl text-sm font-medium hover:shadow-lg hover:shadow-amber-500/25 transition-all duration-300">
                    {t('footer.googlePlay')}
                  </button>
                </div>
              </div>
            </div>
            <div className={`mt-10 pt-6 border-t text-center text-sm ${isDark ? 'border-white/10 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
              {t('footer.rights')}
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}